﻿/* globals jQuery */
/**
 * Numerous tools for working with the editor's "canvas".
 * @module svgcanvas
 *
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria, 2010 Pavol Rusnak, 2010 Jeff Schiller
 *
 */

import { Canvg as canvg } from 'canvg';
import 'pathseg';

import jQueryPluginSVG from './jQuery.attr'; // Needed for SVG attribute setting and array form with `attr`

import * as pathModule from './path';
import * as hstry from './history';
import * as draw from './draw';
import { init as pasteInit, pasteElementsMethod } from './paste-elem';

// eslint-disable-next-line no-duplicate-imports
import {
    identifyLayers, createLayer, cloneLayer, deleteCurrentLayer,
    setCurrentLayer, renameCurrentLayer, setCurrentLayerPosition,
    setLayerVisibility, setLayerLocked, moveSelectedToLayer, mergeLayerWithBelow, mergeAllLayers,
    leaveContext, setContext, setElementLocked
} from './draw';
import { svgRootElement } from './svgroot';
import { init as undoInit, getUndoManager, changeSelectedAttributeNoUndoMethod, changeSelectedAttributeMethod, ffClone, changeSelectedAttributesMethod, bulkChangeAttributesMethod } from './undo';
import {
    init as selectionInit, clearSelectionMethod, addToSelectionMethod, getMouseTargetMethod,
    getIntersectionListMethod, runExtensionsMethod, groupSvgElem, prepareSvg,
    recalculateAllSelectedDimensions, setRotationAngle, removeFromSelectionMethod
} from './selection';
import { init as textActionsInit, textActionsMethod } from './text-actions';
import { init as eventInit, mouseMoveEvent, mouseUpEvent, dblClickEvent, mouseDownEvent, DOMMouseScrollEvent } from './event';
import { init as jsonInit, getJsonFromSvgElements, addSVGElementsFromJson } from './json';
import {
    init as elemInit, getResolutionMethod, getTitleMethod, setGroupTitleMethod,
    setDocumentTitleMethod, setResolutionMethod, getEditorNSMethod, setBBoxZoomMethod,
    setZoomMethod, setColorMethod, setGradientMethod, findDuplicateGradient,
    setStrokeWidthMethod, setStrokeAttrMethod, getBoldMethod, setBoldMethod, getItalicMethod,
    setItalicMethod, setTextAnchorMethod, getFontFamilyMethod, setFontFamilyMethod, setFontColorMethod, getFontColorMethod,
    getFontSizeMethod, setFontSizeMethod, getTextMethod, setTextContentMethod,
    setImageURLMethod, setLinkURLMethod, setRectRadiusMethod, makeHyperlinkMethod,
    removeHyperlinkMethod, setSegTypeMethod, setBackgroundMethod
} from './elem-get-set';
import {
    init as selectedElemInit, moveToTopSelectedElem, moveToBottomSelectedElem,
    moveUpDownSelected, moveSelectedElements, cloneSelectedElements, alignSelectedElements,
    deleteSelectedElements, copySelectedElements, groupSelectedElements, pushGroupProperty,
    ungroupSelectedElement, cycleElement, updateCanvas, distributeSelectedElements, groupUngroupSelectedElements
} from './selected-elem';
import {
    init as blurInit, setBlurNoUndo, setBlurOffsets, setBlur
} from './blur-event';
import { sanitizeSvg } from './sanitize';
import { getReverseNS, NS } from '../common/namespaces';
import {
    text2xml, assignAttributes, cleanupElement, getElem, getUrlFromAttr,
    findDefs, getHref, setHref, getRefElem, getRotationAngle, getPathBBox,
    preventClickDefault, walkTree, getBBoxOfElementAsPath, convertToPath, encode64, decode64,
    getVisibleElements, dropXMLInternalSubset, init as utilsInit,
    getBBox as utilsGetBBox, getStrokedBBoxDefaultVisible, isNullish, blankPageObjectURL,
    $id, $qa, $qq, BBox
} from './utilities';
import {
    transformPoint, matrixMultiply, hasMatrixTransform, transformListToTransform,
    isIdentity, transformBox, XYObject
} from './math';
import { convertToNum, getTypeMap, init as unitsInit, Unit } from '../common/units';
import {
    svgCanvasToString, svgToString, setSvgString, save, exportPDF, setUseDataMethod,
    init as svgInit, importSvgString, embedImage, rasterExport,
    uniquifyElemsMethod, removeUnusedDefElemsMethod, convertGradientsMethod, SaveOptions
} from './svg-exec';
import { isChrome, isWebkit } from '../common/browser'; // , supportsEditableText
import { getTransformList } from './svgtransformlist';
import { remapElement, init as coordsInit } from './coords';
import { recalculateDimensions, init as recalculateInit } from './recalculate';
import { getSelectorManager, Selector, init as selectInit, SelectorManager } from './select';
import { clearSvgContentElementInit, init as clearInit } from './clear';
import { getClosest, getParents, mergeDeep } from '../editor/jgraduate/Util.js';
import { Command, BatchCommand, ChangeElementCommand, InsertElementCommand, MoveElementCommand, RemoveElementCommand } from './undocommands';
import { InputState } from './input-state';
import { runUntracked } from './mutation-tracking';

const $ = jQueryPluginSVG(jQuery);

const visElems = 'a,circle,ellipse,foreignObject,g,image,line,path,polygon,polyline,rect,svg,text,tspan,use';
const refAttrs = ['clip-path', 'fill', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'stroke'];

const anyWindow = window as any;

if (!window.console) {
    anyWindow.console = {};
    window.console.log = function (_str) { /* empty fn */ };
    window.console.dir = function (_str) { /* empty fn */ };
}

if (anyWindow.opera) {
    window.console.log = function (str) { anyWindow.opera.postError(str); };
    window.console.dir = function (_str) { /* empty fn */ };
}

export interface IDataStorage {
    _storage: WeakMap<object, any>;
    put(element, key, obj);
    get(element, key): any
    has(element, key): boolean;
    remove(element, key): any;
}

export interface StyleOptions {
    [key: string]: any;
    fill: string;
    fill_opacity: Float;
    stroke: string;
    stroke_width: Float;
    stroke_dasharray: string;
    stroke_linejoin: string;
    stroke_linecap: string;
    stroke_opacity: Float;
    opacity: Float;
}

export interface TextStyleOptions extends StyleOptions {
    font_size: string | number;
    font_family: string;
}

type BindableEventHandler = (window: Window, ...args: any[]) => void;

// Reenable after fixing eslint-plugin-jsdoc to handle
/**
* The main SvgCanvas class that manages all SVG-related functions.
* @memberof module:svgcanvas
*
* @borrows module:coords.remapElement as #remapElement
* @borrows module:recalculate.recalculateDimensions as #recalculateDimensions
*
* @borrows module:utilities.cleanupElement as #cleanupElement
* @borrows module:utilities.getStrokedBBoxDefaultVisible as #getStrokedBBox
* @borrows module:utilities.getVisibleElements as #getVisibleElements
* @borrows module:utilities.findDefs as #findDefs
* @borrows module:utilities.getUrlFromAttr as #getUrlFromAttr
* @borrows module:utilities.getHref as #getHref
* @borrows module:utilities.setHref as #setHref
* @borrows module:utilities.getRotationAngle as #getRotationAngle
* @borrows module:utilities.getBBox as #getBBox
* @borrows module:utilities.getElem as #getElem
* @borrows module:utilities.getRefElem as #getRefElem
* @borrows module:utilities.assignAttributes as #assignAttributes
*
* @borrows module:SVGTransformList.getTransformList as #getTransformList
* @borrows module:math.matrixMultiply as #matrixMultiply
* @borrows module:math.hasMatrixTransform as #hasMatrixTransform
* @borrows module:math.transformListToTransform as #transformListToTransform
* @borrows module:units.convertToNum as #convertToNum
* @borrows module:sanitize.sanitizeSvg as #sanitizeSvg
* @borrows module:path.pathActions.linkControlPoints as #linkControlPoints
*/
export default class SvgCanvas {
    /**
     * "document" element associated with the container (same as window.document using default svg-editor.js)
     * NOTE: This is not actually a SVG document, but an HTML document.
     */
    private domDocument = window.document;

    private domDocumentContainer: HTMLElement = null;

    // Default configuration options
    private curConfig: any = { // TODO: Fix as any
        show_outside_canvas: true,
        selectNew: true,
        dimensions: [640, 480]
    }

    private allProperties: { shape: StyleOptions, text: TextStyleOptions }; // TODO: Fix as any

    private get curShape() { return this.allProperties.shape; }
    private get curText() { return this.allProperties.text; }
    private curProperties;

    /**
     * This canvas' owning editor
     */
    public editor = null;

    /**
    * This object manages selectors for us.
    * @name module:svgcanvas.SvgCanvas#selectorManager
    * @type {module:select.SelectorManager}
    */
    public selectorManager: SelectorManager = null;

    public undoMgr = getUndoManager(this);

    public inputState: InputState = new InputState();

    /**
    * Current `draw.Drawing` object.
    * @type {module:draw.Drawing}
    * @name module:svgcanvas.SvgCanvas#current_drawing_
    */
    private current_drawing_: draw.Drawing;

    /** This is a container for the document being edited, not the document itself. */
    private svgroot: SVGSVGElement;

    /** This is the document being edited. */
    private svgcontent: SVGSVGElement;

    /** Prefix string for element IDs */
    private idprefix: string = "ELEMENT_";

    private selectedElements: SVGGraphicsElement[] = [];

    /**
    * Float displaying the current zoom level (1 = 100%, .5 = 50%, etc.).
    * @type {Float}
    */
    private currentZoom = 1;

    public contentW: number;
    public contentH: number;

    /** Object to contain image data for raster images that were found encodable */
    private encodableImages: PlainObject = {};

    /** Object with save options */
    private saveOptions: SaveOptions = { round_digits: 5 };

    /** Object with IDs for imported files, to see if one was already added */
    private importIds: PlainObject = {};

    /** Object to contain all included extensions */
    private extensions: PlainObject = {};

    /** Map of deleted reference elements */
    private removedElements: PlainObject = {};

    /** String with an element's initial transform attribute value */
    private startTransform: string = null; // TODO: Fix as any

    /** String indicating the current editor mode */
    private currentMode: string = "select";

    /** String with the current direction in which an element is being resized */
    private currentResizeMode: string = "none";

    /**  The DOM element that was just selected */
    private justSelected: SVGElement = null;

    /** DOM element for selection rectangle drawn by the user */
    private rubberBox: SVGRectElement = null;

    /** Array of current BBoxes, used in getIntersectionList(). */
    private curBBoxes: BBox[] = [];

    /** Canvas point for the most recent right click */
    private lastClickPoint: any = null;  // TODO: Fix as any

    /** Object to contain editor event names and callback functions */
    private events: { [key: string]: BindableEventHandler[] } = {};

    private drawnPath: any = null; // TODO: Fix as any

    /** pointer to current group (for in-group editing) */
    private currentGroup: SVGGElement = null;

    /** Root Current Transformation Matrix in user units */
    private rootSctm: DOMMatrix = null;

    /** String with image URL of last loadable image */
    private lastGoodImgUrl: string = "lovo.svg";

    /** Interface strings, usually for title elements */
    private uiStrings: PlainObject = {};

    private CLIPBOARD_ID = 'svgedit_clipboard';

    /**
    * @param {HTMLElement} container - The container HTML element that should hold the SVG root element
    * @param {module:SVGeditor.configObj.curConfig} config - An object that contains configuration data
    */
    constructor(container, config) {
        this.domDocumentContainer = container;

        // Update config with new one if given
        if (config) {
            $.extend(this.curConfig, config);
        }

        // This is a container for the document being edited, not the document itself.
        /**
         * @name module:svgcanvas~svgroot
         * @type {SVGSVGElement}
         */
        this.svgroot = svgRootElement(this.domDocument, this.curConfig.dimensions);
        container.append(this.svgroot);

        /**
         * The actual element that represents the final output SVG element.
         * @name module:svgcanvas~svgcontent
         * @type {SVGSVGElement}
         */
        this.svgcontent = this.domDocument.createElementNS(NS.SVG, 'svg');

        clearInit(this);
        this.clearSvgContentElement();


        /**
        * Current `draw.Drawing` object.
        * @type {module:draw.Drawing}
        * @name module:svgcanvas.SvgCanvas#current_drawing_
        */
        this.current_drawing_ = new draw.Drawing(this.svgcontent, this.idprefix);

        let shapeProps: StyleOptions = {
            fill: (this.curConfig.initFill.color === 'none' ? '' : '#') + this.curConfig.initFill.color,
            fill_paint: null,
            fill_opacity: this.curConfig.initFill.opacity,
            stroke: '#' + this.curConfig.initStroke.color,
            stroke_paint: null,
            stroke_opacity: this.curConfig.initStroke.opacity,
            stroke_width: this.curConfig.initStroke.width,
            stroke_dasharray: 'none',
            stroke_linejoin: 'miter',
            stroke_linecap: 'butt',
            opacity: this.curConfig.initOpacity
        };
        let textProps: TextStyleOptions = $.extend(true, {
            fill: '#000000',
            stroke_width: this.curConfig.text && this.curConfig.text.stroke_width,
            font_size: this.curConfig.text && this.curConfig.text.font_size,
            font_family: this.curConfig.text && this.curConfig.text.font_family
        }, shapeProps);

        // Object containing data for the currently selected styles
        this.allProperties = {
            shape: shapeProps,
            text: textProps,
        };


        jsonInit(this);

        /**
        * Initialize from units.ts.
        * Send in an object implementing the ElementContainer interface (see units.ts).
        */
        unitsInit(this, {
            getElement: getElem,
            getHeight: () => parseFloat(this.svgcontent.getAttribute('height')) / this.currentZoom,
            getWidth: () => parseFloat(this.svgcontent.getAttribute('width')) / this.currentZoom,
        }
        );

        /**
    * This should actually be an intersection as all interfaces should be met.
    * @type {module:utilities.EditorContext#getSVGRoot|module:recalculate.EditorContext#getSVGRoot|module:coords.EditorContext#getSVGRoot|module:path.EditorContext#getSVGRoot}
    */

        utilsInit(this);
        coordsInit(this);
        recalculateInit(this);

        selectInit(this);

        this.selectorManager = getSelectorManager();

        undoInit(
            /**
        * @implements {module:undo.undoContext}
        */
            {
                call: (ev, arg) => this.call(ev, arg),
                restoreRefElems: (elem) => this.restoreRefElems,
                getSVGContent: () => this.getSVGContent(),
                getCanvas: () => this,
                getCurrentMode: () => this.getCurrentMode(),
                getCurrentZoom: () => this.getCurrentZoom(),
                getSVGRoot: () => this.getSVGRoot(),
                getSelectedElements: () => this.getSelectedElements(),
            }
        );


        selectionInit(
            /**
        * @implements {module:selection.selectionContext}
        */
            {
                getCanvas: () => this,
                getDataStorage: () => this.getDataStorage(),
                getCurrentGroup: () => this.currentGroup,
                getSelectedElements: () => this.getSelectedElements(),
                getSVGRoot: () => this.getSVGRoot(),
                getSVGContent: () => this.getSVGContent(),
                getDOMContainer() { return container; },
                getExtensions: () => this.extensions,
                setExtensions: (key, value) => this.extensions[key] = value,
                getCurrentZoom: () => this.getCurrentZoom(),
                getRubberBox: () => this.rubberBox,
                setCurBBoxes: (value) => this.curBBoxes = value,
                getCurBBoxes: (_value) => this.curBBoxes,
                getCurrentResizeMode: () => this.currentResizeMode,
                addCommandToHistory: (cmd: Command) => this.addCommandToHistory(cmd),
                getSelector() { return Selector; }
            }
        );

        pathModule.init(this,
            /**
        * @implements {module:path.EditorContext}
        */
            {
                resetD: (p) => {
                    if (typeof this.pathActions.convertPath === 'function') {
                        p.setAttribute('d', this.pathActions.convertPath(p));
                    } else if (typeof this.pathActions.convertPaths === 'function') {
                        p.setAttribute('d', this.pathActions.convertPaths(p));
                    }
                },
                remapElement,
                getContainer() {
                    return container;
                },
                getRubberBox: () => this.rubberBox,
                setRubberBox: (rb) => this.rubberBox = rb,
                addPtsToSelection: ({ closedSubpath, grips }) => {
                    // TODO: Correct this:
                    this.pathActions.canDeleteNodes = true;
                    this.pathActions.closed_subpath = closedSubpath;
                    this.call('pointsAdded', { closedSubpath, grips });
                    this.call('selected', grips);
                },
                endChanges: ({ cmd, elem }) => {
                    this.addCommandToHistory(cmd);
                    this.call('changed', [elem]);
                },
                getDrawnPath: () => this.drawnPath,
                setDrawnPath: (dp) => this.drawnPath = dp,
            }
        );

        // Animation element to change the opacity of any newly created element
        const opacAni = document.createElementNS(NS.SVG, 'animate');
        opacAni.setAttribute('attributeName', 'opacity');
        opacAni.setAttribute('begin', 'indefinite');
        opacAni.setAttribute('dur', "1");
        opacAni.setAttribute('fill', 'freeze');
        this.svgroot.appendChild(opacAni);

        this.lastGoodImgUrl = this.curConfig.imgPath + 'logo.svg';
        this.curProperties = this.curShape;


        // Mouse events
        (() => {
            const freehand = {
                minx: null,
                miny: null,
                maxx: null,
                maxy: null
            };
            const THRESHOLD_DIST = 0.8,
                STEP_COUNT = 10;
            let dAttr = null;
            let startX = null;
            let startY = null;
            let rStartX = null;
            let rStartY = null;
            let initBbox = {};
            let sumDistance = 0;
            const controllPoint2 = { x: 0, y: 0 };
            const controllPoint1 = { x: 0, y: 0 };
            let start = { x: 0, y: 0 };
            const end = { x: 0, y: 0 };
            let bSpline = { x: 0, y: 0 };
            let nextPos = { x: 0, y: 0 };
            let parameter;
            let nextParameter;

            /**
            * @function eventInit Initialize from event.ts
            * @returns {void}
            */
            eventInit(this, 
                /**
                * @implements {module:event.eventContext_}
                */
                {
                    getStartX() { return startX; },
                    setStartX(value) { startX = value; },
                    getStartY() { return startY; },
                    setStartY(value) { startY = value; },
                    getRStartX() { return rStartX; },
                    getRStartY() { return rStartY; },
                    getRubberBox: () => this.rubberBox,
                    getInitBbox() { return initBbox; },
                    getCurrentResizeMode: () => this.currentResizeMode,
                    getDrawnPath: () => this.drawnPath,
                    getJustSelected: () => this.justSelected,
                    getOpacAni() { return opacAni; },
                    getParameter() { return parameter; },
                    getNextParameter() { return nextParameter; },
                    getStepCount() { return STEP_COUNT; },
                    getThreSholdDist() { return THRESHOLD_DIST; },
                    getSumDistance() { return sumDistance; },
                    getStart(key) { return start[key]; },
                    getEnd(key) { return end[key]; },
                    getbSpline(key) { return bSpline[key]; },
                    getNextPos(key) { return nextPos[key]; },
                    getControllPoint1(key) { return controllPoint1[key]; },
                    getControllPoint2(key) { return controllPoint2[key]; },
                    getFreehand(key) { return freehand[key]; },
                    getDAttr() { return dAttr; },
                    getLastGoodImgUrl: () => this.lastGoodImgUrl,
                    getCurText: (key) => this.curText[key],
                    setDAttr(value) { dAttr = value; },
                    setEnd(key, value) { end[key] = value; },
                    setControllPoint1(key, value) { controllPoint1[key] = value; },
                    setControllPoint2(key, value) { controllPoint2[key] = value; },
                    setJustSelected: (value) => this.justSelected = value,
                    setParameter(value) { parameter = value; },
                    setStart(value) { start = value; },
                    setRStartX(value) { rStartX = value; },
                    setRStartY(value) { rStartY = value; },
                    setSumDistance(value) { sumDistance = value; },
                    setbSpline(value) { bSpline = value; },
                    setNextPos(value) { nextPos = value; },
                    setNextParameter(value) { nextParameter = value; },
                    setCurProperties: (key, value) => this.curProperties[key] = value,
                    setCurText: (key, value) => this.curText[key] = value,
                    setStartTransform: (transform) => this.startTransform = transform,
                    setFreehand(key, value) { freehand[key] = value; },
                    setCurBBoxes: (value) => this.curBBoxes = value,
                    setRubberBox: (value) => this.rubberBox = value,
                    setInitBbox(value) { initBbox = value; },
                    setCurrentResizeMode: (value: string) => this.currentResizeMode = value,
                    setLastClickPoint: (value) => this.lastClickPoint = value,
                }
            );

            // Added mouseup to the container here.
            // TODO(codedread): Figure out why after the Closure compiler, the window mouseup is ignored.
            container.addEventListener('mousedown', this.mouseDown);
            container.addEventListener('mousemove', this.mouseMove);
            // prevent links from being followed in the canvas
            container.addEventListener('click', (e) => { e.preventDefault(); return false; });
            container.addEventListener('dblclick', this.dblClick);
            //container.addEventListener('mouseup', this.mouseUp);
            document.addEventListener('mouseup', this.mouseUp);

            // TODO(rafaelcastrocouto): User preference for shift key and zoom factor
            container.addEventListener('mousewheel', DOMMouseScrollEvent);
            container.addEventListener('DOMMouseScroll', DOMMouseScrollEvent);

        })();

        textActionsInit(this);

        const nsMap = getReverseNS();

        svgInit(this,
            /**
            * @implements {module:elem-get-set.elemInit}
            */
            {
                getNsMap() { return nsMap; },
                getVisElems() { return visElems; },
                getImportIds: (key) => this.importIds[key],
                setImportIds: (key, value) => this.importIds[key] = value,
                getrefAttrs() { return refAttrs; },
                getcanvg() { return canvg; },
            }
        );

        draw.init(this);
        elemInit(this);

        /**
    * Group: Element Styling.
    */

        /**
    * @typedef {PlainObject} module:svgcanvas.PaintOptions
    * @property {"solidColor"} type
    */

        (() => {
            let curCommand = null;
            let filter = null;
            let filterHidden = false;

            blurInit(
                /**
                * @implements {module:elem-get-set.elemInit}
                */
                {
                    getCanvas: () => this,
                    getCurCommand: () => curCommand,
                    setCurCommand: (value) => curCommand = value,
                    getFilter: () => filter,
                    setFilter: (value) => filter = value,
                    getFilterHidden: () => filterHidden,
                    setFilterHidden: (value) => filterHidden = value,
                    changeSelectedAttributeNoUndoMethod,
                    changeSelectedAttributeMethod,
                    isWebkit,
                    addCommandToHistory: (cmd: Command) => this.addCommandToHistory(cmd),
                    getSelectedElements: () => this.getSelectedElements(),
                }
            );
        })();

        this.contentW = this.getResolution().w;
        this.contentH = this.getResolution().h;

        selectedElemInit(
            /**
        * @implements {module:selected-elem.elementContext}
        */
            {
                getSelectedElements: () => this.getSelectedElements(),
                addCommandToHistory: (cmd: Command) => this.addCommandToHistory(cmd),
                getJsonFromSvgElement: (data: SVGElement) => this.getJsonFromSvgElement(data),
                addSVGElementFromJson: (data: any) => this.addSVGElementFromJson(data),
                changeSelectedAttribute: (attr, val, elems) => this.changeSelectedAttribute(attr, val, elems),
                flashStorage: () => this.flashStorage(),
                call: (ev, arg) => this.call(ev, arg),
                getIntersectionList: (rect: DOMRect) => this.getIntersectionList(rect),
                setCurBBoxes: (value) => this.curBBoxes = value,
                getSVGRoot: () => this.getSVGRoot(),
                gettingSelectorManager: () => this.selectorManager,
                getCurrentZoom: () => this.getCurrentZoom(),
                getDrawing: () => this.getCurrentDrawing(),
                getCurrentGroup: () => this.getCurrentGroup(),
                addToSelection: (elemsToAdd: SVGGraphicsElement[], showGrips: boolean) => this.addToSelection(elemsToAdd, showGrips),
                getContentW: () => this.contentW,
                getContentH: () => this.contentH,
                getClipboardID: () => this.getClipboardID(),
                getDOMDocument: () => this.getDOMDocument(),
                clearSelection: (noCall?: boolean) => this.clearSelection(noCall),
                getNextId: () => this.getNextId(),
                selectOnly: (elems: SVGGraphicsElement[], showGrips?: boolean) => this.selectOnly(elems, showGrips),
                uniquifyElems: (g: SVGElement) => this.uniquifyElems(g),
                setUseData: (parent: SVGElement) => this.setUseData(parent),
                convertGradients: (elem: SVGElement) => this.convertGradients(elem),
                getSVGContent: () => this.getSVGContent(),
                getCanvas: () => this,
                getDataStorage: () => this.getDataStorage(),
                getVisElems() { return visElems; },
                getMultiselected: () => this.editor.multiselected,
            }
        );

        // Listen for changes to localStorage.
        window.addEventListener('storage', (ev) => this.storageChange(ev), false);
        // Ask other tabs for sessionStorage (this is ONLY to trigger event).
        localStorage.setItem(this.CLIPBOARD_ID + '_startup', Math.random().toString());

        /**
        * @function pasteInit Initialize from paste-elem.ts.
        * @returns {void}
        * paste element functionality
        */
        pasteInit(
            /**
            * @implements {module:event.eventContext_}
            */
            {
                getCanvas: () => this,
                getClipboardID: () => this.getClipboardID(),
                getLastClickPoint: (key) => this.lastClickPoint[key],
                addCommandToHistory: (cmd: Command) => this.addCommandToHistory(cmd),
                restoreRefElems: (elem: SVGElement) => this.restoreRefElems(elem)
            }
        );

        this.clear();
    } // End constructor

    public $id = $id;
    public $qq = $qq;
    public $qa = $qa;
    public mergeDeep = mergeDeep;
    public getClosest = getClosest;
    public getParents = getParents;
    public isLayer = draw.Layer.isLayer;
    public getJsonFromSvgElement = getJsonFromSvgElements;
    public addSVGElementFromJson = addSVGElementsFromJson;
    public getTransformList = getTransformList;
    public matrixMultiply = matrixMultiply;
    public hasMatrixTransform = hasMatrixTransform;
    public transformListToTransform = transformListToTransform;
    public convertToNum = convertToNum;
    public findDefs = findDefs;
    public getUrlFromAttr = getUrlFromAttr;
    public getHref = getHref;
    public setHref = setHref;
    public getBBox = utilsGetBBox;
    public getRotationAngle = getRotationAngle;
    public getElem = getElem;
    public getRefElem = getRefElem;
    public assignAttributes = assignAttributes;
    public cleanupElement = cleanupElement;
    public remapElement = remapElement;
    public recalculateDimensions = recalculateDimensions;
    public sanitizeSvg = sanitizeSvg;
    public getMouseTarget = getMouseTargetMethod;
    public pathActions = pathModule.pathActions;
    public runExtensions = runExtensionsMethod;
    public runExtension = (name, action, vars) => this.runExtensions(action, vars, false, (n) => n === name);
    public getStrokedBBox = getStrokedBBoxDefaultVisible;
    public getVisibleElements = getVisibleElements;
    public textActions = textActionsMethod;
    public identifyLayers = identifyLayers;
    public createLayer = createLayer;
    public cloneLayer = cloneLayer;
    public deleteCurrentLayer = deleteCurrentLayer;
    public setCurrentLayer = setCurrentLayer;
    public renameCurrentLayer = renameCurrentLayer;
    public setCurrentLayerPosition = setCurrentLayerPosition;
    public setLayerVisibility = setLayerVisibility;
    public setLayerLocked = setLayerLocked;
    public setElementLocked = setElementLocked;
    public moveSelectedToLayer = moveSelectedToLayer;
    public mergeLayerWithBelow = mergeLayerWithBelow;
    public mergeAllLayers = mergeAllLayers;
    public leaveContext = leaveContext;
    public setContext = setContext;
    public get linkControlPoints() { return this.pathActions.linkControlPoints };
    public getResolution = getResolutionMethod;

    /**
    * This function makes the changes to the elements. It does not add the change
    * to the history stack.
    * @param {string} attr - Attribute name
    * @param {string|Float} newValue - String or number with the new attribute value
    * @param {Element[]} elems - The DOM elements to apply the change to
    * @returns {void}
    */
    public changeSelectedAttributeNoUndo = changeSelectedAttributeNoUndoMethod;

    /**
    * Change the given/selected element and add the original value to the history stack.
    * If you want to change all `selectedElements`, ignore the `elems` argument.
    * If you want to change only a subset of `selectedElements`, then send the
    * subset to this function in the `elems` argument.
    * @function module:svgcanvas.SvgCanvas#changeSelectedAttribute
    * @param {string} attr - String with the attribute name
    * @param {string|Float} val - String or number with the new attribute value
    * @param {Element[]} elems - The DOM elements to apply the change to
    * @returns {void}
    */
    public changeSelectedAttribute = changeSelectedAttributeMethod;

    /**
    * Change the given/selected element and add the original value to the history stack.
    * If you want to change all `selectedElements`, ignore the `elems` argument.
    * If you want to change only a subset of `selectedElements`, then send the
    * subset to this function in the `elems` argument.
    * @function module:svgcanvas.SvgCanvas#changeSelectedAttribute
    * @param {string} attrValMap - A map of attributes and values to apply to the element
    * @param {Element[]} elems - The DOM elements to apply the change to
    * @returns {void}
    */
    public changeSelectedAttributes = changeSelectedAttributesMethod;

    public bulkChangeAttributes = bulkChangeAttributesMethod;

    /**
    * This function resets the svgcontent element while keeping it in the DOM.
    * @function module:svgcanvas.SvgCanvas#clearSvgContentElement
    * @returns {void}
    */
    public clearSvgContentElement = clearSvgContentElementInit;

    /**
    * Clears the selection. The 'selected' handler is then optionally called.
    * @name module:svgcanvas.SvgCanvas#clearSelection
    * @type {module:draw.DrawCanvasInit#clearSelection|module:path.EditorContext#clearSelection}
    * @fires module:svgcanvas.SvgCanvas#event:selected
    */
    public clearSelection = clearSelectionMethod;

    /** Root Current Transformation Matrix in user units */
    public getRootSctm() {
        return this.rootSctm;
    }

    /** Root Current Transformation Matrix in user units */
    public setRootSctm(newValue: any) {
        this.rootSctm = newValue;
    }

    /**
    * Adds a list of elements to the selection. The 'selected' handler is then called.
    * @name module:svgcanvas.SvgCanvas#addToSelection
    * @type {module:path.EditorContext#addToSelection}
    * @fires module:svgcanvas.SvgCanvas#event:selected
    */
    public addToSelection = addToSelectionMethod;

    /**
    * Removes elements from the selection.
    * @function module:svgcanvas.SvgCanvas#removeFromSelection
    * @param {Element[]} elemsToRemove - An array of elements to remove from selection
    * @returns {void}
    */
    public removeFromSelection = removeFromSelectionMethod;

    /**
    * Looks at DOM elements inside the `<defs>` to see if they are referred to,
    * removes them from the DOM if they are not.
    * @function module:svgcanvas.SvgCanvas#removeUnusedDefElems
    * @returns {Integer} The number of elements that were removed
    */
    public removeUnusedDefElems = removeUnusedDefElemsMethod;

    /**
    * Main function to set up the SVG content for output.
    * @function module:svgcanvas.SvgCanvas#svgCanvasToString
    * @returns {string} The SVG image for output
    */
    public svgCanvasToString = svgCanvasToString;

    /**
    * Sub function ran on each SVG element to convert it to a string as desired.
    * @function module:svgcanvas.SvgCanvas#svgToString
    * @param {Element} elem - The SVG element to convert
    * @param {Integer} indent - Number of spaces to indent this tag
    * @returns {string} The given element as an SVG tag
    */
    public svgToString = svgToString;

    /**
    * Converts a given image file to a data URL when possible, then runs a given callback.
    * @function module:svgcanvas.SvgCanvas#embedImage
    * @param {string} src - The path/URL of the image
    * @returns {Promise<string|false>} Resolves to a Data URL (string|false)
    */
    public embedImage = embedImage;

    /**
    * @function module:svgcanvas.SvgCanvas#getTitle
    * @param {Element} [elem]
    * @returns {string|void} the current group/SVG's title contents or
    * `undefined` if no element is passed nd there are no selected elements.
    */
    public getTitle = getTitleMethod;

    /**
    * Sets the group/SVG's title content.
    * @function module:svgcanvas.SvgCanvas#setGroupTitle
    * @param {string} val
    * @todo Combine this with `setDocumentTitle`
    * @returns {void}
    */
    public setGroupTitle = setGroupTitleMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getDocumentTitle
    * @returns {string|void} The current document title or an empty string if not found
    */
    public getDocumentTitle() { return this.getTitle(this.svgcontent); }

    /**
    * Adds/updates a title element for the document with the given name.
    * This is an undoable action.
    * @function module:svgcanvas.SvgCanvas#setDocumentTitle
    * @param {string} newTitle - String with the new title
    * @returns {void}
    */
    public setDocumentTitle = setDocumentTitleMethod;

    /**
    * Returns the editor's namespace URL, optionally adding it to the root element.
    * @function module:svgcanvas.SvgCanvas#getEditorNS
    * @param {boolean} [add] - Indicates whether or not to add the namespace value
    * @returns {string} The editor's namespace URL
    */
    public getEditorNS = getEditorNSMethod;

    /**
    * Changes the document's dimensions to the given size.
    * @function module:svgcanvas.SvgCanvas#setResolution
    * @param {Float|"fit"} x - Number with the width of the new dimensions in user units.
    * Can also be the string "fit" to indicate "fit to content".
    * @param {Float} y - Number with the height of the new dimensions in user units.
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {boolean} Indicates if resolution change was successful.
    * It will fail on "fit to content" option with no content to fit to.
    */
    public setResolution = setResolutionMethod;

    /**
    * Sets the zoom level on the canvas-side based on the given value.
    * @function module:svgcanvas.SvgCanvas#setBBoxZoom
    * @param {"selection"|"canvas"|"content"|"layer"|module:SVGEditor.BBoxObjectWithFactor} val - Bounding box object to zoom to or string indicating zoom option. Note: the object value type is defined in `svg-editor.js`
    * @param {Integer} editorW - The editor's workarea box's width
    * @param {Integer} editorH - The editor's workarea box's height
    * @returns {module:svgcanvas.ZoomAndBBox|void}
    */
    public setBBoxZoom = setBBoxZoomMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getOffset
    * @returns {module:svgcanvas.ElementPositionInCanvas} An object with `x`, `y` values indicating the svgcontent element's
    * position in the editor's canvas.
    */
    public getOffset(): XYObject {
        return {
            x: Number(this.svgcontent.getAttribute('x')),
            y: Number(this.svgcontent.getAttribute('y'))
        };
    };

    /**
    * Update configuration options with given values.
    * @function module:svgcanvas.SvgCanvas#setConfig
    * @param {module:SVGEditor.Config} opts - Object with options
    * @returns {void}
    */
    public setConfig(opts) {
        Object.assign(this.curConfig, opts);
    };

    public getIdPrefix() {
        return this.idprefix;
    }

    /**
    * Changes the ID prefix to the given value.
    * @function module:svgcanvas.SvgCanvas#setIdPrefix
    * @param {string} p - String with the new prefix
    * @returns {void}
    */
    public setIdPrefix(p) {
        this.idprefix = p;
    }

    /**
     * Same as getMode()
     * @returns {string} The current editor mode string
     */
    public getCurrentMode() {
        return this.currentMode;
    }

    /**
     * Same as getCurrentMode()
     * @returns {string} The current editor mode string
     */
    public getMode() {
        return this.currentMode;
    }

    /**
    * Sets the editor's mode to the given string. DIFFERENT FROM setMode! This only sets currentMode.
     * @param newMode
     */
    public setCurrentMode(newMode: string) {
        this.currentMode = newMode;

        const ev = new CustomEvent("editor-mode-changed", { detail: newMode });
        window.dispatchEvent(ev);

        return this.currentMode;
    }

    /**
    * Sets the editor's mode to the given string. DIFFERENT FROM setCurrentMode! This also resets pathActions, textActions and curProperties.
    * @function module:svgcanvas.SvgCanvas#setMode
    * @param {string} name - String with the new mode to change to
    * @returns {void}
    */
    public setMode(name) {
        this.pathActions.clear(true);
        this.textActions.clear();
        this.curProperties = (this.selectedElements[0] && this.selectedElements[0].nodeName === 'text') ? this.curText : this.curShape;
        this.currentMode = name;
        document.body.setAttribute("data-mode", name);
        const ev = new CustomEvent("editor-mode-changed", { detail: name });
        window.dispatchEvent(ev);
    }

    public getCurrentConfig() {
        return this.curConfig;
    }

    /**
    * Returns the current Drawing.
    * @name module:svgcanvas.SvgCanvas#getCurrentDrawing
    * @type {module:draw.DrawCanvasInit#getCurrentDrawing}
    */
    public getCurrentDrawing() {
        return this.current_drawing_;
    }

    public getDOMDocument() {
        return this.domDocument;
    }

    public getDOMDocumentContainer() {
        return this.domDocumentContainer;
    }

    /** Same as getRootElem() */
    public getSVGRoot() {
        return this.svgroot;
    }

    /** Same as getSVGRoot() */
    public getRootElem() {
        return this.svgroot;
    };

    /** Same as getContentElem() */
    public getSVGContent() {
        return this.svgcontent;
    }

    /** Same as getSVGContent() */
    public getContentElem() {
        return this.svgcontent;
    }

    public getSelectedElements() {
        return this.selectedElements;
    }

    public setSelectedElements(key, value) {
        this.selectedElements[key] = value;
    }

    public setEmptySelectedElements() {
        this.selectedElements = [];
    }

    public getCurrentGroup() {
        return this.currentGroup;
    }

    public setCurrentGroup(newGroup: SVGGElement) {
        this.currentGroup = newGroup;
        return this.currentGroup;
    }

    public getCurrentShape() {
        return this.curShape;
    }

    public getCurText() {
        return this.curText;
    }

    public getSaveOptions() {
        return this.saveOptions;
    }

    public setSaveOption(key: string, value: any) {
        this.saveOptions[key] = value;
    }

    public getStartTransform() {
        return this.startTransform;
    }

    public setStartTransform(newTransform) {
        this.startTransform = newTransform;
        return this.startTransform;
    }

    /**
    * Sets the zoom to the given level.
    * @function module:svgcanvas.SvgCanvas#setZoom
    * @param {Float} zoomLevel - Float indicating the zoom level to change to
    * @fires module:svgcanvas.SvgCanvas#event:ext_zoomChanged
    * @returns {void}
    */
    public setZoom = setZoomMethod;

    public setCurrentZoom(newZoom: number) {
        this.currentZoom = newZoom;
        return this.currentZoom;
    }

    public getCurrentZoom() {
        return this.currentZoom;
    }

    public round(val: Float) {
        return Number.parseInt((val * this.currentZoom).toString()) / this.currentZoom;
    }

    public getPathActions() {
        return this.pathActions;
    }

    public getUndoMgr() {
        return this.undoMgr;
    }

    public getNextId() {
        return this.getCurrentDrawing().getNextId();
    }

    public getId() {
        return this.getCurrentDrawing().getId();
    }

    /**
    * Sets a given URL to be a "last good image" URL.
    * @function module:svgcanvas.SvgCanvas#setGoodImage
    * @param {string} val
    * @returns {void}
    */
    public setGoodImage(val: string) {
        this.lastGoodImgUrl = val;
        return this.lastGoodImgUrl;
    }

    public getGoodImage() {
        return this.lastGoodImgUrl;
    }

    public getColor(type) {
        return this.curProperties[type];
    }

    /**
    * Change the current stroke/fill color/gradient value.
    * @function module:svgcanvas.SvgCanvas#setColor
    * @param {string} type - String indicating fill or stroke
    * @param {string} val - The value to set the stroke attribute to
    * @param {boolean} preventUndo - Boolean indicating whether or not this should be an undoable option
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setColor = setColorMethod;

    /**
    * Apply the current gradient to selected element's fill or stroke.
    * @function module:svgcanvas.SvgCanvas#setGradient
    * @param {"fill"|"stroke"} type - String indicating "fill" or "stroke" to apply to an element
    * @returns {void}
    */
    public setGradient = setGradientMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getStrokeWidth
    * @returns {Float|string} The current stroke-width value
    */
    public getStrokeWidth() {
        return this.curProperties.stroke_width;
    };

    /**
    * Sets the stroke width for the current selected elements.
    * When attempting to set a line's width to 0, this changes it to 1 instead.
    * @function module:svgcanvas.SvgCanvas#setStrokeWidth
    * @param {Float} val - A Float indicating the new stroke width value
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setStrokeWidth = setStrokeWidthMethod;

    /**
    * Set the given stroke-related attribute the given value for selected elements.
    * @function module:svgcanvas.SvgCanvas#setStrokeAttr
    * @param {string} attr - String with the attribute name
    * @param {string|Float} val - String or number with the attribute value
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setStrokeAttr = setStrokeAttrMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getStyle
    * @returns {module:svgcanvas.StyleOptions} current style options
    */
    public getStyle() {
        return this.curShape;
    };

    public getOpacity() {
        return this.curShape.opacity;
    }

    /**
    * Sets the given opacity on the current selected elements.
    * @function module:svgcanvas.SvgCanvas#setOpacity
    * @param {string} val
    * @returns {void}
    */
    public setOpacity(val) {
        this.curShape.opacity = val;
        this.changeSelectedAttribute('opacity', val);
    }

    /**
    * @function module:svgcanvas.SvgCanvas#getFillOpacity
    * @returns {Float} the current fill opacity
    */
    public getFillOpacity() {
        return this.curShape.fill_opacity;
    }

    /**
    * @function module:svgcanvas.SvgCanvas#getStrokeOpacity
    * @returns {string} the current stroke opacity
    */
    public getStrokeOpacity() {
        return this.curShape.stroke_opacity;
    }

    /**
    * Sets the current fill/stroke opacity.
    * @function module:svgcanvas.SvgCanvas#setPaintOpacity
    * @param {string} type - String with "fill" or "stroke"
    * @param {Float} val - Float with the new opacity value
    * @param {boolean} preventUndo - Indicates whether or not this should be an undoable action
    * @returns {void}
    */
    public setPaintOpacity(type: "fill" | "stroke", val: Float, preventUndo: boolean) {
        this.curShape[type + '_opacity'] = val;

        if (!preventUndo) {
            this.changeSelectedAttribute(type + '-opacity', val);
        } else {
            this.changeSelectedAttributeNoUndo(type + '-opacity', val);
        }
    }

    /**
    * Gets the `stdDeviation` blur value of the given element.
    * @function module:svgcanvas.SvgCanvas#getBlur
    * @param {Element} elem - The element to check the blur value for
    * @returns {string} stdDeviation blur attribute value
    */
    public getBlur(elem: SVGGraphicsElement | string) {
        let val = 0;

        if (typeof elem === 'string') {
            elem = getElem(elem) as SVGGraphicsElement;
        }

        if (elem) {
            const filterUrl = elem.getAttribute('filter');
            if (filterUrl) {
                const blur = getElem(elem.id + '_blur');
                if (blur) {
                    val = Number((blur.firstChild as Element).getAttribute('stdDeviation'));
                }
            }
        }

        return val;
    }

    /**
      * Sets the `stdDeviation` blur value on the selected element without being undoable.
      * @function module:svgcanvas.SvgCanvas#setBlurNoUndo
      * @param {Float} val - The new `stdDeviation` value
      * @returns {void}
      */
    public setBlurNoUndo = setBlurNoUndo;

    /**
    * Sets the `x`, `y`, `width`, `height` values of the filter element in order to
    * make the blur not be clipped. Removes them if not neeeded.
    * @function module:svgcanvas.SvgCanvas#setBlurOffsets
    * @param {Element} filterElem - The filter DOM element to update
    * @param {Float} stdDev - The standard deviation value on which to base the offset size
    * @returns {void}
    */
    public setBlurOffsets = setBlurOffsets;

    /**
    * Adds/updates the blur filter to the selected element.
    * @function module:svgcanvas.SvgCanvas#setBlur
    * @param {Float} val - Float with the new `stdDeviation` blur value
    * @param {boolean} complete - Whether or not the action should be completed (to add to the undo manager)
    * @returns {void}
    */
    public setBlur = setBlur;

    /**
    * Gets the current fill/stroke opacity.
    * @function module:svgcanvas.SvgCanvas#getPaintOpacity
    * @param {"fill"|"stroke"} type - String with "fill" or "stroke"
    * @returns {Float} Fill/stroke opacity
    */
    public getPaintOpacity(type) {
        return type === 'fill' ? this.getFillOpacity() : this.getStrokeOpacity();
    }

    /**
    * Check whether selected element is bold or not.
    * @function module:svgcanvas.SvgCanvas#getBold
    * @returns {boolean} Indicates whether or not element is bold
    */
    public getBold = getBoldMethod;

    /**
    * Make the selected element bold or normal.
    * @function module:svgcanvas.SvgCanvas#setBold
    * @param {boolean} b - Indicates bold (`true`) or normal (`false`)
    * @returns {void}
    */
    public setBold = setBoldMethod;

    /**
    * Check whether selected element is in italics or not.
    * @function module:svgcanvas.SvgCanvas#getItalic
    * @returns {boolean} Indicates whether or not element is italic
    */
    public getItalic = getItalicMethod;

    /**
    * Make the selected element italic or normal.
    * @function module:svgcanvas.SvgCanvas#setItalic
    * @param {boolean} i - Indicates italic (`true`) or normal (`false`)
    * @returns {void}
    */
    public setItalic = setItalicMethod;

    /**
    * Set the new text anchor.
    * @function module:svgcanvas.SvgCanvas#setTextAnchor
    * @param {string} textAnchor - The value of the text anchor (start, middle or end)
    * @returns {void}
    */
    public setTextAnchor = setTextAnchorMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getFontFamily
    * @returns {string} The current font family
    */
    public getFontFamily = getFontFamilyMethod;

    /**
    * Set the new font family.
    * @function module:svgcanvas.SvgCanvas#setFontFamily
    * @param {string} val - String with the new font family
    * @returns {void}
    */
    public setFontFamily = setFontFamilyMethod;

    /**
    * Set the new font color.
    * @function module:svgcanvas.SvgCanvas#setFontColor
    * @param {string} val - String with the new font color
    * @returns {void}
    */
    public setFontColor = setFontColorMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getFontColor
    * @returns {string} The current font color
    */
    public getFontColor = getFontColorMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getFontSize
    * @returns {Float} The current font size
    */
    public getFontSize = getFontSizeMethod;

    /**
    * Applies the given font size to the selected element.
    * @function module:svgcanvas.SvgCanvas#setFontSize
    * @param {Float} val - Float with the new font size
    * @returns {void}
    */
    public setFontSize = setFontSizeMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#getText
    * @returns {string} The current text (`textContent`) of the selected element
    */
    public getText = getTextMethod;

    /**
    * Updates the text element with the given string.
    * @function module:svgcanvas.SvgCanvas#setTextContent
    * @param {string} val - String with the new text
    * @returns {void}
    */
    public setTextContent = setTextContentMethod;

    /**
    * Sets the new image URL for the selected image element. Updates its size if
    * a new URL is given.
    * @function module:svgcanvas.SvgCanvas#setImageURL
    * @param {string} val - String with the image URL/path
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setImageURL = setImageURLMethod;

    /**
    * Sets the new link URL for the selected anchor element.
    * @function module:svgcanvas.SvgCanvas#setLinkURL
    * @param {string} val - String with the link URL/path
    * @returns {void}
    */
    public setLinkURL = setLinkURLMethod;

    /**
    * Sets the `rx` and `ry` values to the selected `rect` element
    * to change its corner radius.
    * @function module:svgcanvas.SvgCanvas#setRectRadius
    * @param {string|Float} val - The new radius
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setRectRadius = setRectRadiusMethod;

    /**
    * Wraps the selected element(s) in an anchor element or converts group to one.
    * @function module:svgcanvas.SvgCanvas#makeHyperlink
    * @param {string} url
    * @returns {void}
    */
    public makeHyperlink = makeHyperlinkMethod;

    /**
    * @function module:svgcanvas.SvgCanvas#removeHyperlink
    * @returns {void}
    */
    public removeHyperlink = removeHyperlinkMethod;

    /**
    * Sets the new segment type to the selected segment(s).
    * @function module:svgcanvas.SvgCanvas#setSegType
    * @param {Integer} newType - New segment type. See {@link https://www.w3.org/TR/SVG/paths.html#InterfaceSVGPathSeg} for list
    * @returns {void}
    */
    public setSegType = setSegTypeMethod;

    /**
    * Convert selected element to a path, or get the BBox of an element-as-path.
    * @function module:svgcanvas.SvgCanvas#convertToPath
    * @todo (codedread): Remove the getBBox argument and split this function into two.
    * @param {Element} elem - The DOM element to be converted
    * @param {boolean} getBBox - Boolean on whether or not to only return the path's BBox
    * @returns {void|DOMRect|false|SVGPathElement|null} If the getBBox flag is true, the resulting path's bounding box object.
    * Otherwise the resulting path element is returned.
    */
    public convertToPath(elem?: SVGGraphicsElement, getBBox?: boolean) {
        if (isNullish(elem)) {
            for (const el of this.selectedElements) {
                if (el) {
                    this.convertToPath(el);
                }
            }
            return undefined;
        }

        if (getBBox) {
            return getBBoxOfElementAsPath(elem, this.addSVGElementFromJson, this.pathActions);
        }

        // TODO: Why is this applying attributes from curShape, then inside utilities.convertToPath it's pulling addition attributes from elem?
        // TODO: If convertToPath is called with one elem, curShape and elem are probably the same; but calling with multiple is a bug or cool feature.
        const attrs = {
            fill: this.curShape.fill,
            'fill-opacity': this.curShape.fill_opacity,
            stroke: this.curShape.stroke,
            'stroke-width': this.curShape.stroke_width,
            'stroke-dasharray': this.curShape.stroke_dasharray,
            'stroke-linejoin': this.curShape.stroke_linejoin,
            'stroke-linecap': this.curShape.stroke_linecap,
            'stroke-opacity': this.curShape.stroke_opacity,
            opacity: this.curShape.opacity,
            visibility: 'hidden'
        };

        return convertToPath(
            elem, attrs, this.addSVGElementFromJson, this.pathActions,
            this.clearSelection, this.addToSelection, hstry, this.addCommandToHistory
        );
    };

    public getClipboardID() {
        return this.CLIPBOARD_ID;
    }

    /**
    * Removes all selected elements from the DOM and adds the change to the
    * history stack.
    * @function module:svgcanvas.SvgCanvas#deleteSelectedElements
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public deleteSelectedElements = deleteSelectedElements;

    /**
    * Remembers the current selected elements on the clipboard.
    * @function module:svgcanvas.SvgCanvas#copySelectedElements
    * @returns {void}
    */
    public copySelectedElements = copySelectedElements;

    /**
    * Removes all selected elements from the DOM and adds the change to the
    * history stack. Remembers removed elements on the clipboard.
    * @function module:svgcanvas.SvgCanvas#cutSelectedElements
    * @returns {void}
    */
    public cutSelectedElements() {
        this.copySelectedElements();
        this.deleteSelectedElements();
    };

    /**
    * @function module:svgcanvas.SvgCanvas#pasteElements
    * @param {"in_place"|"point"|void} type
    * @param {Integer|void} x Expected if type is "point"
    * @param {Integer|void} y Expected if type is "point"
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @fires module:svgcanvas.SvgCanvas#event:ext_IDsUpdated
    * @returns {void}
    */
    public pasteElements = pasteElementsMethod;

    public canPasteElements() {
        let clipb = JSON.parse(sessionStorage.getItem(this.getClipboardID()));
        if (!clipb || !Array.isArray(clipb)) {
            return false;
        }

        return true;
    }

    public groupUngroupSelectedElements = groupUngroupSelectedElements;

    /**
    * Wraps all the selected elements in a group (`g`) element.
    * @function module:svgcanvas.SvgCanvas#groupSelectedElements
    * @param {"a"|"g"} [type="g"] - type of element to group into, defaults to `<g>`
    * @param {string} [urlArg]
    * @returns {void}
    */
    public groupSelectedElements = groupSelectedElements;

    /**
    * Pushes all appropriate parent group properties down to its children, then
    * removes them from the group.
    * @function module:svgcanvas.SvgCanvas#pushGroupProperties
    * @param {SVGAElement|SVGGElement} g
    * @param {boolean} undoable
    * @returns {BatchCommand|void}
    */
    public pushGroupProperties = pushGroupProperty;

    /**
    * Unwraps all the elements in a selected group (`g`) element. This requires
    * significant recalculations to apply group's transforms, etc. to its children.
    * @function module:svgcanvas.SvgCanvas#ungroupSelectedElement
    * @returns {void}
    */
    public ungroupSelectedElement = ungroupSelectedElement;

    /**
    * Repositions the selected element to the bottom in the DOM to appear on top of
    * other elements.
    * @function module:svgcanvas.SvgCanvas#moveToTopSelectedElement
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public moveToTopSelectedElement = moveToTopSelectedElem;

    /**
    * Repositions the selected element to the top in the DOM to appear under
    * other elements.
    * @function module:svgcanvas.SvgCanvas#moveToBottomSelectedElement
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public moveToBottomSelectedElement = moveToBottomSelectedElem;

    /**
    * Moves the select element up or down the stack, based on the visibly
    * intersecting elements.
    * @function module:svgcanvas.SvgCanvas#moveUpDownSelected
    * @param {"Up"|"Down"} dir - String that's either 'Up' or 'Down'
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public moveUpDownSelected = moveUpDownSelected;

    /**
    * Moves selected elements on the X/Y axis.
    * @function module:svgcanvas.SvgCanvas#moveSelectedElements
    * @param {Float} dx - Float with the distance to move on the x-axis
    * @param {Float} dy - Float with the distance to move on the y-axis
    * @param {boolean} undoable - Boolean indicating whether or not the action should be undoable
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {BatchCommand|void} Batch command for the move
    */
    public moveSelectedElements = moveSelectedElements;

    /**
    * Create deep DOM copies (clones) of all selected elements and move them slightly
    * from their originals.
    * @function module:svgcanvas.SvgCanvas#cloneSelectedElements
    * @param {Float} x Float with the distance to move on the x-axis
    * @param {Float} y Float with the distance to move on the y-axis
    * @returns {void}
    */
    public cloneSelectedElements = cloneSelectedElements;

    /**
    * Aligns selected elements.
    * @function module:svgcanvas.SvgCanvas#alignSelectedElements
    * @param {string} type - String with single character indicating the alignment type
    * @param {"selected"|"largest"|"smallest"|"page"} relativeTo
    * @returns {void}
    */
    public alignSelectedElements = alignSelectedElements;

    public distributeSelectedElements = distributeSelectedElements;

    /**
    * Updates the editor canvas width/height/position after a zoom has occurred.
    * @function module:svgcanvas.SvgCanvas#updateCanvas
    * @param {Float} w - Float with the new width
    * @param {Float} h - Float with the new height
    * @fires module:svgcanvas.SvgCanvas#event:ext_canvasUpdated
    * @returns {module:svgcanvas.CanvasInfo}
    */
    public updateCanvas = updateCanvas;

    /**
    * Set the background of the editor (NOT the actual document).
    * @function module:svgcanvas.SvgCanvas#setBackground
    * @param {string} color - String with fill color to apply
    * @param {string} url - URL or path to image to use
    * @returns {void}
    */
    public setBackground = setBackgroundMethod;

    /**
    * Select the next/previous element within the current layer.
    * @function module:svgcanvas.SvgCanvas#cycleElement
    * @param {boolean} next - true = next and false = previous element
    * @fires module:svgcanvas.SvgCanvas#event:selected
    * @returns {void}
    */
    public cycleElement = cycleElement;


    public addCommandToHistory(cmd: Command) {
        this.undoMgr.addCommandToHistory(cmd);
    }

    /**
    * Does nothing by default, handled by optional widget/extension.
    * @function module:svgcanvas.SvgCanvas#open
    * @returns {boolean}
    */
    public open = async function (onOpenCallback) {
        /* empty fn */
        if (onOpenCallback) {
            onOpenCallback();
        }
        return false;
    };

    /**
    * Serializes the current drawing into SVG XML text and passes it to the 'saved' handler.
    * This function also includes the XML prolog. Clients of the `SvgCanvas` bind their save
    * function to the 'saved' event.
    * @function module:svgcanvas.SvgCanvas#save
    * @param {module:svgcanvas.SaveOptions} opts
    * @fires module:svgcanvas.SvgCanvas#event:saved
    * @returns {void}
    */
    public save = save;

    /**
    * Generates a PNG (or JPG, BMP, WEBP) Data URL based on the current image,
    * then calls "exported" with an object including the string, image
    * information, and any issues found.
    * @function module:svgcanvas.SvgCanvas#rasterExport
    * @param {"PNG"|"JPEG"|"BMP"|"WEBP"|"ICO"} [imgType="PNG"]
    * @param {Float} [quality] Between 0 and 1
    * @param {string} [exportWindowName]
    * @param {PlainObject} [opts]
    * @param {boolean} [opts.avoidEvent]
    * @fires module:svgcanvas.SvgCanvas#event:exported
    * @todo Confirm/fix ICO type
    * @returns {Promise<module:svgcanvas.ImageExportedResults>} Resolves to {@link module:svgcanvas.ImageExportedResults}
    */
    public rasterExport = rasterExport;

    /**
    * Generates a PDF based on the current image, then calls "exportedPDF" with
    * an object including the string, the data URL, and any issues found.
    * @function module:svgcanvas.SvgCanvas#exportPDF
    * @param {string} [exportWindowName] Will also be used for the download file name here
    * @param {external:jsPDF.OutputType} [outputType="dataurlstring"]
    * @fires module:svgcanvas.SvgCanvas#event:exportedPDF
    * @returns {Promise<module:svgcanvas.PDFExportedResults>} Resolves to {@link module:svgcanvas.PDFExportedResults}
    */
    public exportPDF = exportPDF;

    /**
    * Returns the current drawing as raw SVG XML text.
    * @function module:svgcanvas.SvgCanvas#getSvgString
    * @returns {string} The current drawing as raw SVG XML text.
    */
    public getSvgString() {
        this.saveOptions.apply = false;
        return this.svgCanvasToString();
    };

    /**
    * This function sets the current drawing as the input SVG XML.
    * @function module:svgcanvas.SvgCanvas#setSvgString
    * @param {string} xmlString - The SVG as XML text.
    * @param {boolean} [preventUndo=false] - Indicates if we want to do the
    * changes without adding them to the undo stack - e.g. for initializing a
    * drawing on page load.
    * @fires module:svgcanvas.SvgCanvas#event:setnonce
    * @fires module:svgcanvas.SvgCanvas#event:unsetnonce
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {boolean} This function returns `false` if the set was
    *     unsuccessful, `true` otherwise.
    */
    public setSvgString = setSvgString;

    /**
    * This function imports the input SVG XML as a `<symbol>` in the `<defs>`, then adds a
    * `<use>` to the current layer.
    * @function module:svgcanvas.SvgCanvas#importSvgString
    * @param {string} xmlString - The SVG as XML text.
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {null|Element} This function returns null if the import was unsuccessful, or the element otherwise.
    * @todo
    * - properly handle if namespace is introduced by imported content (must add to svgcontent
    * and update all prefixes in the imported node)
    * - properly handle recalculating dimensions, `recalculateDimensions()` doesn't handle
    * arbitrary transform lists, but makes some assumptions about how the transform list
    * was obtained
    */
    public importSvgString = importSvgString;

    /**
    * This function determines whether to use a nonce in the prefix, when
    * generating IDs for future documents in SVG-Edit.
    * If you're controlling SVG-Edit externally, and want randomized IDs, call
    * this BEFORE calling `svgCanvas.setSvgString`.
    * @function module:svgcanvas.SvgCanvas#randomizeIds
    * @param {boolean} [enableRandomization] If true, adds a nonce to the prefix. Thus
    * `svgCanvas.randomizeIds() <==> svgCanvas.randomizeIds(true)`
    * @returns {void}
    */
    public randomizeIds(enableRandomization?: boolean) {
        if (arguments.length > 0 && enableRandomization === false) {
            draw.randomizeIds(false, this.getCurrentDrawing());
        } else {
            draw.randomizeIds(true, this.getCurrentDrawing());
        }
    };

    /**
    * Ensure each element has a unique ID.
    * @function module:svgcanvas.SvgCanvas#uniquifyElems
    * @param {Element} g - The parent element of the tree to give unique IDs
    * @returns {void}
    */
    public uniquifyElems = uniquifyElemsMethod;

    /**
    * Assigns reference data for each use element.
    * @function module:svgcanvas.SvgCanvas#setUseData
    * @param {Element} parent
    * @returns {void}
    */
    public setUseData = setUseDataMethod;

    /**
    * Converts gradients from userSpaceOnUse to objectBoundingBox.
    * @function module:svgcanvas.SvgCanvas#convertGradients
    * @param {Element} elem
    * @returns {void}
    */
    public convertGradients = convertGradientsMethod;

    /**
    * This method sends back an array or a NodeList full of elements that
    * intersect the multi-select rubber-band-box on the currentLayer only.
    *
    * We brute-force `getIntersectionList` for browsers that do not support it (Firefox).
    *
    * Reference:
    * Firefox does not implement `getIntersectionList()`, see {@link https://bugzilla.mozilla.org/show_bug.cgi?id=501421}.
    * @function module:svgcanvas.SvgCanvas#getIntersectionList
    * @param {SVGRect} rect
    * @returns {Element[]|NodeList} Bbox elements
    */
    public getIntersectionList = getIntersectionListMethod;

    /**
    * Wrap an SVG element into a group element, mark the group as 'gsvg'.
    * @function module:svgcanvas.SvgCanvas#groupSvgElem
    * @param {Element} elem - SVG element to wrap
    * @returns {void}
    */
    public groupSvgElem = groupSvgElem;

    /**
    * Runs the SVG Document through the sanitizer and then updates its paths.
    * @function module:svgcanvas.SvgCanvas#prepareSvg
    * @param {XMLDocument} newDoc - The SVG DOM document
    * @returns {void}
    */
    public prepareSvg = prepareSvg;

    /**
    * Removes any old rotations if present, prepends a new rotation at the
    * transformed center.
    * @function module:svgcanvas.SvgCanvas#setRotationAngle
    * @param {string|Float} val - The new rotation angle in degrees
    * @param {boolean} preventUndo - Indicates whether the action should be undoable or not
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public setRotationAngle = setRotationAngle;

    /**
    * Runs `recalculateDimensions` on the selected elements,
    * adding the changes to a single batch command.
    * @function module:svgcanvas.SvgCanvas#recalculateAllSelectedDimensions
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @returns {void}
    */
    public recalculateAllSelectedDimensions = recalculateAllSelectedDimensions;

    /**
    * Follows these conditions:
    * - When we are in a create mode, the element is added to the canvas but the
    *   action is not recorded until mousing up.
    * - When we are in select mode, select the element, remember the position
    *   and do nothing else.
    * @param {MouseEvent} evt
    * @fires module:svgcanvas.SvgCanvas#event:ext_mouseDown
    * @returns {void}
    */
    public mouseDown = mouseDownEvent;

    /**
    * in this function we do not record any state changes yet (but we do update
    * any elements that are still being created, moved or resized on the canvas)
    * @param {MouseEvent} evt
    * @fires module:svgcanvas.SvgCanvas#event:transition
    * @fires module:svgcanvas.SvgCanvas#event:ext_mouseMove
    * @returns {void}
    */
    public mouseMove = mouseMoveEvent;

    /**
    * - in create mode, the element's opacity is set properly, we create an InsertElementCommand
    * and store it on the Undo stack
    * - in move/resize mode, the element's attributes which were affected by the move/resize are
    * identified, a ChangeElementCommand is created and stored on the stack for those attrs
    * this is done in when we recalculate the selected dimensions()
    * @param {MouseEvent} evt
    * @fires module:svgcanvas.SvgCanvas#event:zoomed
    * @fires module:svgcanvas.SvgCanvas#event:changed
    * @fires module:svgcanvas.SvgCanvas#event:ext_mouseUp
    * @returns {void}
    */
    public mouseUp = mouseUpEvent;

    public dblClick = dblClickEvent;

    /**
    * Clears the current document. This is not an undoable action.
    * @function module:svgcanvas.SvgCanvas#clear
    * @fires module:svgcanvas.SvgCanvas#event:cleared
    * @returns {void}
    */
    public clear() {
        // Replaces the whole document, which Blazor takes as a new baseline (see svgcontent-changed), rather than as
        // individual changes.
        runUntracked(() => this.clearInternal());
    }

    private clearInternal() {
        this.pathActions.clear();

        this.clearSelection();

        // clear the svgcontent node
        this.clearSvgContentElement();

        // create new document
        this.current_drawing_ = new draw.Drawing(this.svgcontent);

        // create empty first layer
        this.createLayer('Layer 1');

        // clear the undo stack
        this.undoMgr.resetUndoStack();

        // reset the selector manager
        this.selectorManager.initGroup();

        // reset the rubber band box
        this.rubberBox = this.selectorManager.getRubberBandBox();

        this.call('cleared');
        window.dispatchEvent(new CustomEvent("svgcontent-changed", { detail: this.getContentElem() }));
    };

    public getHasMouseActionStarted() {
        return this.inputState.getStarted();
    }


    /**
    * Add an extension to the editor.
    * @function module:svgcanvas.SvgCanvas#addExtension
    * @param {string} name - String with the ID of the extension. Used internally; no need for i18n.
    * @param {module:svgcanvas.ExtensionInitCallback} [extInitFunc] - Function supplied by the extension with its data
    * @param {module:svgcanvas.ExtensionInitArgs} initArgs
    * @fires module:svgcanvas.SvgCanvas#event:extension_added
    * @throws {TypeError|Error} `TypeError` if `extInitFunc` is not a function, `Error`
    *   if extension of supplied name already exists
    * @returns {Promise<void>} Resolves to `undefined`
    */
    public addExtension = async (name, extInitFunc, { $: jq, importLocale }) => {
        if (typeof extInitFunc !== 'function') {
            throw new TypeError('Function argument expected for `svgcanvas.addExtension`');
        }
        if (name in this.extensions) {
            throw new Error('Cannot add extension "' + name + '", an extension by that name already exists.');
        }
        // Provide private vars/funcs here. Is there a better way to do this?
        /**
     * @typedef {module:svgcanvas.PrivateMethods} module:svgcanvas.ExtensionArgumentObject
     * @property {SVGSVGElement} svgroot See {@link module:svgcanvas~svgroot}
     * @property {SVGSVGElement} svgcontent See {@link module:svgcanvas~svgcontent}
     * @property {!(string|Integer)} nonce See {@link module:draw.Drawing#getNonce}
     * @property {module:select.SelectorManager} selectorManager
     * @property {module:SVGEditor~ImportLocale} importLocale
     */
        /**
     * @type {module:svgcanvas.ExtensionArgumentObject}
     * @see {@link module:svgcanvas.PrivateMethods} source for the other methods/properties
     */
        const argObj = $.extend(this.getPrivateMethods(), {
            $: jq,
            importLocale,
            svgroot: this.getSVGRoot(),
            svgcontent: this.getSVGContent(),
            nonce: this.getCurrentDrawing().getNonce(),
            selectorManager: this.selectorManager
        });

        const extObj = await extInitFunc(argObj);
        if (extObj) {
            extObj.name = name;
        }

        this.extensions[name] = extObj;
        return this.call('extension_added', extObj);
    };

    /**
    * Group: Selection.
    */

    /**
    * Selects only the given elements, shortcut for `clearSelection(); addToSelection()`.
    * @function module:svgcanvas.SvgCanvas#selectOnly
    * @param {SVGGraphicsElement[]} elems - an array of DOM elements to be selected
    * @param {boolean} showGrips - Indicates whether the resize grips should be shown
    * @returns {void}
    */
    public selectOnly(elems: SVGGraphicsElement[], showGrips: boolean = true) {
        this.clearSelection(true);
        this.addToSelection(elems, showGrips);
    }

    /**
    * Clears the selection, then adds all elements in the current layer to the selection.
    * @function module:svgcanvas.SvgCanvas#selectAllInCurrentLayer
    * @returns {void}
    */
    public selectAllInCurrentLayer() {
        const currentLayer = this.getCurrentDrawing().getCurrentLayer();

        if (currentLayer) {
            this.currentMode = 'select';
            if (this.currentGroup) {
                this.selectOnly(this.currentGroup.children as unknown as SVGGraphicsElement[]);
            } else {
                this.selectOnly(currentLayer.children as unknown as SVGGraphicsElement[]);
            }
        }
    }

    /**
    * Illustrator-style "Select Same": selects every element in the current layer/group
    * that shares the same fill, stroke, or overall appearance (fill + stroke + stroke
    * width) as the currently selected reference element.
    * @function module:svgcanvas.SvgCanvas#selectSame
    * @param {"fill"|"stroke"|"appearance"} kind - Which property to match on.
    * @returns {void}
    */
    public selectSame(kind: 'fill' | 'stroke' | 'appearance') {
        const reference = this.getSelectedElements()[0];
        if (!reference) {
            return;
        }

        const parent = this.currentGroup || this.getCurrentDrawing().getCurrentLayer();
        if (!parent) {
            return;
        }

        // Match on the panel-designer colour attributes (pe-fill/pe-stroke), falling back
        // to the raw SVG fill/stroke so untagged elements still compare sensibly.
        const matchKey = (el: Element): string => {
            const fill = el.getAttribute('pe-fill') ?? el.getAttribute('fill') ?? '';
            const stroke = el.getAttribute('pe-stroke') ?? el.getAttribute('stroke') ?? '';
            if (kind === 'fill') { return fill; }
            if (kind === 'stroke') { return stroke; }
            return `${fill}|${stroke}|${el.getAttribute('stroke-width') ?? ''}`;
        };

        const targetKey = matchKey(reference);
        const matches: SVGElement[] = [];
        for (const child of Array.from(parent.children)) {
            // Skip non-graphical nodes (defs, title, etc.).
            if (typeof (child as SVGGraphicsElement).getBBox !== 'function') {
                continue;
            }
            if (matchKey(child) === targetKey) {
                matches.push(child as SVGElement);
            }
        }

        if (matches.length > 0) {
            this.currentMode = 'select';
            this.selectOnly(matches);
        }
    }


    public setBaseUnit(unit: Unit) {
        this.editor.configObj.curConfig.baseUnit = unit;
        this.setConfig(this.editor.configObj.curConfig);

        const ev = new CustomEvent("editor-units-changed", { detail: this.editor.configObj.curConfig.baseUnit });
        window.dispatchEvent(ev);

        this.editor.updateCanvas();
    }

    public getBaseUnit(): Unit {
        return this.curConfig.baseUnit;
    }

    /** Same as getSnapToGrid() */
    public getGridSnapping() {
        return this.curConfig.gridSnapping;
    }

    /** Same as getGridSnapping() */
    public getSnapToGrid() {
        return this.curConfig.gridSnapping;
    }

    public getVersion() {
        return 'svgcanvas.js ($Rev$)';
    }

    /**
    * Update interface strings with given values.
    * @function module:svgcanvas.SvgCanvas#setUiStrings
    * @param {module:path.uiStrings} strs - Object with strings (see the [locales API]{@link module:locale.LocaleStrings} and the [tutorial]{@tutorial LocaleDocs})
    * @returns {void}
    */
    public setUiStrings(strs) {
        Object.assign(this.uiStrings, strs.notification);
        pathModule.setUiStrings(strs);
    };

    public getUIStrings() {
        return this.uiStrings;
    }

    /** A storage solution aimed at replacing jQuerys data function.
     * Implementation Note: Elements are stored in a (WeakMap)[https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakMap].
     * This makes sure the data is garbage collected when the node is removed.
     */
    public dataStorage: IDataStorage = {
        _storage: new WeakMap(),
        put: function (element, key, obj) {
            if (!this._storage.has(element)) {
                this._storage.set(element, new Map());
            }
            this._storage.get(element).set(key, obj);
        },
        get: function (element, key) {
            const s = this._storage.get(element);
            if (!s) {
                console.error(`Could not find storage ${key} for element`, element);
                return undefined; // TODO: Should throw?
            }
            return s.get(key);
        },
        has: function (element, key) {
            return this._storage.has(element) && this._storage.get(element).has(key);
        },
        remove: function (element, key) {
            const ret = this._storage.get(element).delete(key);
            if (this._storage.get(element).size !== 0) {
                this._storage.delete(element);
            }
            return ret;
        }
    };

    public getDataStorage() {
        return this.dataStorage;
    }

    /**
    * @deprecated getPrivateMethods
    * Since all methods are/should be public somehow, this function should be removed;
    *  we might require `import` in place of this in the future once ES6 Modules
    *  widespread

    * Being able to access private methods publicly seems wrong somehow,
    * but currently appears to be the best way to allow testing and provide
    * access to them to plugins.
    * @function module:svgcanvas.SvgCanvas#getPrivateMethods
    * @returns {module:svgcanvas.PrivateMethods}
    */
    public getPrivateMethods() {
        return {
            addCommandToHistory: (cmd: Command) => this.addCommandToHistory(cmd),
            BatchCommand,
            ChangeElementCommand,
            decode64,
            dropXMLInternalSubset,
            encode64,
            ffClone,
            findDefs,
            findDuplicateGradient,
            getElem,
            getPathBBox,
            getTypeMap,
            getUrlFromAttr,
            identifyLayers: draw.identifyLayers,
            InsertElementCommand,
            isChrome,
            isIdentity,
            logMatrix: (m: any) => this.logMatrix(m),
            MoveElementCommand,
            NS,
            preventClickDefault,
            RemoveElementCommand,
            text2xml,
            transformBox,
            transformPoint,
            walkTree
        };
    };

    /**
    * The "implements" should really be an intersection applying to all types rather than a union.
    * @name module:svgcanvas.SvgCanvas#call
    * @type {module:draw.DrawCanvasInit#call|module:path.EditorContext#call}
    * @deprecated - Use normal window.dispatchEvent instead.
    */
    public call(ev, arg?) {
        if (ev !== "message") {
            window.dispatchEvent(new CustomEvent(ev, { detail: arg }));
        }

        if (!this.events[ev]) {
            return undefined;
        }

        const results = [];

        for (const f of this.events[ev]) {
            results.push(f(window, arg));
        }

        return results;
    }

    /**
    * Attaches a callback function to an event.
    * @function module:svgcanvas.SvgCanvas#bind
    * @param {"changed"|"contextset"|"selected"|"pointsAdded"|"extension_added"|"extensions_added"|"message"|"transition"|"zoomed"|"updateCanvas"|"zoomDone"|"saved"|"exported"|"exportedPDF"|"setnonce"|"unsetnonce"|"cleared"} ev - String indicating the name of the event
    * @param {module:svgcanvas.EventHandler} f - The callback function to bind to the event
    * @deprecated - Use normal window.addEventListener instead.
    */
    public bind(ev: string, f: BindableEventHandler) {
        if (!this.events[ev]) {
            this.events[ev] = [];
        }
        this.events[ev].push(f);
    }

    /**
    * Debug tool to easily see the current matrix in the browser's console.
    * @function module:svgcanvas~logMatrix
    * @param {SVGMatrix} m The matrix
    * @returns {void}
    */
    private logMatrix(m) {
        console.log([m.a, m.b, m.c, m.d, m.e, m.f]);
    };

    private restoreRefElems = (elem: SVGElement) => {
        // Look for missing reference elements, restore any found
        let attrs = {};
        refAttrs.forEach(function (item, _) {
            attrs[item] = elem.getAttribute(item);
        });
        Object.values<string>(attrs).forEach((val) => {
            if (val && val.startsWith('url(')) {
                const id = getUrlFromAttr(val).substring(1);
                const ref = getElem(id);
                if (!ref) {
                    findDefs().append(this.removedElements[id]);
                    delete this.removedElements[id];
                }
            }
        });

        const children = elem.getElementsByTagName('*');

        if (children.length) {
            for (let i = 0, l = children.length; i < l; i++) {
                this.restoreRefElems(children[i] as SVGElement);
            }
        }
    }

    /**
    * Flash the clipboard data momentarily on localStorage so all tabs can see.
    * @returns {void}
    */
    private flashStorage() {
        const data = sessionStorage.getItem(this.CLIPBOARD_ID);
        localStorage.setItem(this.CLIPBOARD_ID, data);
        setTimeout(() => {
            localStorage.removeItem(this.CLIPBOARD_ID);
        }, 1);
    }

    /**
    * Transfers sessionStorage from one tab to another.
    * @param {!Event} ev Storage event.
    * @returns {void}
    */
    private storageChange(ev) {
        if (!ev.newValue) return; // This is a call from removeItem.
        if (ev.key === this.CLIPBOARD_ID + '_startup') {
            // Another tab asked for our sessionStorage.
            localStorage.removeItem(this.CLIPBOARD_ID + '_startup');
            this.flashStorage();
        } else if (ev.key === this.CLIPBOARD_ID) {
            // Another tab sent data.
            sessionStorage.setItem(this.CLIPBOARD_ID, ev.newValue);
        }
    }


    // attach utilities function to the class that are used by SvgEdit so
    // we can avoid using the whole utilities.ts file in svgEdit.js
    public static readonly isNullish = isNullish;
    public static readonly encode64 = encode64;
    public static readonly decode64 = decode64;
    public static readonly $id = $id;
    public static readonly $qq = $qq;
    public static readonly $qa = $qa;
    public static readonly mergeDeep = mergeDeep;
    public static readonly getClosest = getClosest;
    public static readonly getParents = getParents;
    public static readonly blankPageObjectURL = blankPageObjectURL;
} // End class
